<template>
    <div class="activity__event">
        <v-icon
            class="activity__glyph"
            :class="`activity__glyph--${kindClass}`"
            :icon="icon"
            size="18"
        />
        <div class="activity__body">
            <div class="activity__head">
                <span>
                    <UserLink v-if="showActor" :user-id="event.actorId" />
                    {{ verb }}
                    <TicketKey :ticket-key="event.ticketKey" />
                </span>
                <span
                    class="activity__when text-medium-emphasis"
                    :title="formatDateTime(event.date)"
                >
                    {{ relativeTime(event.date) }}
                </span>
            </div>
            <div class="activity__title text-medium-emphasis">{{ event.ticketTitle }}</div>
            <div v-if="event.items.length > 0" class="activity__changes">
                <div v-for="(item, i) in event.items" :key="i" class="activity__change">
                    <span class="activity__field text-medium-emphasis">
                        {{ fieldLabel(item.field) }}
                    </span>
                    <template v-if="isLongTextField(item.field)">
                        <span class="text-medium-emphasis">edited</span>
                    </template>
                    <template v-else>
                        <span
                            class="activity__value activity__value--old"
                            :class="{ activity__none: !item.from }"
                        >
                            {{ item.from || "None" }}
                        </span>
                        <v-icon size="14" class="text-medium-emphasis">mdi-arrow-right</v-icon>
                        <span
                            class="activity__value activity__value--new"
                            :class="{ activity__none: !item.to }"
                        >
                            {{ item.to || "None" }}
                        </span>
                    </template>
                </div>
            </div>
            <div v-if="event.comment" class="activity__quote text-medium-emphasis">
                {{ event.comment }}
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { UserActivityEvent, UserActivityKind } from "@/api/users";
import { formatDateTime } from "@/dateTimeUtils";
import { relativeTime } from "@/utils";
import { computed } from "vue";
import TicketKey from "../common/TicketKey.vue";
import UserLink from "../users/UserLink.vue";
import { fieldLabel, isLongTextField } from "../tickets/detail/ticketHistory";

type Props = {
    event: UserActivityEvent;
    showActor?: boolean;
};

const props = withDefaults(defineProps<Props>(), { showActor: false });

const PRESENTATION: Record<UserActivityKind, { verb: string; icon: string; class: string }> = {
    [UserActivityKind.Created]: {
        verb: "reported",
        icon: "mdi-plus-circle-outline",
        class: "created",
    },
    [UserActivityKind.Changed]: { verb: "edited", icon: "mdi-swap-horizontal", class: "changed" },
    [UserActivityKind.Commented]: {
        verb: "commented on",
        icon: "mdi-comment-outline",
        class: "commented",
    },
    [UserActivityKind.Resolved]: {
        verb: "resolved",
        icon: "mdi-check-circle-outline",
        class: "resolved",
    },
};

const presentation = computed(() => PRESENTATION[props.event.kind]);
const verb = computed(() => presentation.value.verb);
const icon = computed(() => presentation.value.icon);
const kindClass = computed(() => presentation.value.class);
</script>

<style lang="scss">
.activity {
    &__event {
        display: grid;
        grid-template-columns: 24px 1fr;
        gap: 12px;
        padding: 12px 0;

        & + & {
            border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
        }
    }

    &__glyph {
        margin-top: 2px;

        &--created {
            color: rgb(var(--v-theme-success));
        }

        &--changed {
            color: rgb(var(--v-theme-primary));
        }

        &--commented {
            color: rgb(var(--v-theme-freeze));
        }

        &--resolved {
            color: rgb(var(--v-theme-release));
        }
    }

    &__body {
        min-width: 0;
    }

    &__head {
        display: flex;
        align-items: baseline;
        gap: 8px;
        flex-wrap: wrap;
        font-size: 14px;
    }

    &__when {
        font-size: 12px;
        margin-left: auto;
        white-space: nowrap;
    }

    &__title {
        font-size: 13px;
        margin-top: 2px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    &__changes {
        display: flex;
        flex-direction: column;
        gap: 4px;
        margin-top: 6px;
    }

    &__change {
        display: flex;
        align-items: baseline;
        flex-wrap: wrap;
        gap: 8px;
        font-size: 13px;
    }

    &__field {
        min-width: 88px;
    }

    &__value {
        border-radius: 4px;
        padding: 1px 7px;

        &--old {
            background: rgba(var(--v-theme-error), 0.12);
        }

        &--new {
            background: rgba(var(--v-theme-success), 0.12);
        }
    }

    &__none {
        font-style: italic;
        opacity: 0.7;
    }

    &__quote {
        margin-top: 6px;
        font-size: 13px;
        border-left: 2px solid rgba(var(--v-border-color), var(--v-border-opacity));
        padding-left: 10px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}
</style>
